/**
 * Enhanced Video Player Component
 * Full-screen video player with external React-based controls
 * Uses postMessage communication with iframe for clean separation
 * Matches visual style of GsapSlidePreviewEnhanced
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Button } from '../ui/button';
import { ChevronLeft, Maximize2, Minimize2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { VideoPlayerControls } from './VideoPlayerControls';
import { VideoPlayerState, VideoPlayerMessage, SlideSegment, VideoPlayerCommand } from './PlayerTypes';
import { videoPreviewAPI } from '../../services/api';

interface VideoPlayerEnhancedProps {
  conversationId: string;
  onBackToGallery?: () => void;
  className?: string;
}

export const VideoPlayerEnhanced: React.FC<VideoPlayerEnhancedProps> = ({
  conversationId,
  onBackToGallery,
  className = '',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isReady, setIsReady] = useState(false);

  // Player state synced from iframe
  const [playerState, setPlayerState] = useState<VideoPlayerState>({
    isPlaying: false,
    currentTime: 0,
    duration: 0,
    playbackRate: 1,
    volume: 1,
    isMuted: false,
    currentSlide: 0,
    totalSlides: 0,
    segments: [],
  });

  // Build video player URL
  const manifestUrl = videoPreviewAPI.getJSONUrl(conversationId, 'manifest.json');
  const playerUrl = `/player/video-player.html?manifest=${encodeURIComponent(manifestUrl)}`;

  // Listen for postMessage events from iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent<VideoPlayerMessage>) => {
      // Security check
      if (event.origin !== window.location.origin) return;

      const message = event.data;

      if (message.type === 'VIDEO_PLAYER_READY') {
        console.log('🎬 Video player ready:', message.manifest);
        setIsReady(true);

        // Initialize segments from manifest
        let cumulativeTime = 0;
        const segments: SlideSegment[] = message.manifest.slides.map((slide, index) => {
          const segment = {
            slideIndex: index,
            slideId: slide.id,
            title: `Slide ${index + 1}`,
            startTime: cumulativeTime,
            endTime: cumulativeTime + slide.duration,
            duration: slide.duration,
          };
          cumulativeTime += slide.duration; // Update cumulative time for next segment
          return segment;
        });

        setPlayerState(prev => ({
          ...prev,
          duration: message.manifest.totalDuration,
          totalSlides: message.manifest.slideCount,
          segments,
        }));
      } else if (message.type === 'VIDEO_PLAYER_STATE') {
        // Update player state from iframe
        setPlayerState(prev => ({
          ...prev,
          isPlaying: message.state.isPlaying,
          currentTime: message.state.currentTime,
          currentSlide: message.state.currentSlide,
        }));
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Send commands to iframe
  const sendCommand = useCallback((command: VideoPlayerCommand) => {
    if (iframeRef.current?.contentWindow) {
      console.log('📤 Sending command to video player:', command);
      iframeRef.current.contentWindow.postMessage(command, '*');
    }
  }, []);

  // Player control handlers
  const handlePlayPause = useCallback(() => {
    if (playerState.isPlaying) {
      sendCommand({ type: 'PAUSE' });
    } else {
      sendCommand({ type: 'PLAY' });
    }
  }, [playerState.isPlaying, sendCommand]);

  const handleSeek = useCallback((time: number) => {
    sendCommand({ type: 'SEEK', time });
  }, [sendCommand]);

  const handleRestart = useCallback(() => {
    sendCommand({ type: 'RESTART' });
  }, [sendCommand]);

  const handleSlideChange = useCallback((slideIndex: number) => {
    sendCommand({ type: 'GO_TO_SLIDE', slideIndex });
  }, [sendCommand]);

  const handlePreviousSlide = useCallback(() => {
    const prevSlide = Math.max(0, playerState.currentSlide - 1);
    handleSlideChange(prevSlide);
  }, [playerState.currentSlide, handleSlideChange]);

  const handleNextSlide = useCallback(() => {
    const nextSlide = Math.min(playerState.totalSlides - 1, playerState.currentSlide + 1);
    handleSlideChange(nextSlide);
  }, [playerState.currentSlide, playerState.totalSlides, handleSlideChange]);

  const handleSpeedChange = useCallback((rate: number) => {
    sendCommand({ type: 'SET_SPEED', rate });
  }, [sendCommand]);

  const handleFullscreen = useCallback(() => {
    if (!isFullscreen) {
      containerRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }, [isFullscreen]);

  // Handle fullscreen change events
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if typing in input/textarea
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      switch (e.key) {
        case ' ':
          e.preventDefault();
          handlePlayPause();
          break;
        case 'r':
        case 'R':
          handleRestart();
          break;
        case 'f':
        case 'F':
          handleFullscreen();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          handlePreviousSlide();
          break;
        case 'ArrowRight':
          e.preventDefault();
          handleNextSlide();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handlePlayPause, handleRestart, handleFullscreen, handlePreviousSlide, handleNextSlide]);

  return (
    <div
      ref={containerRef}
      className={cn(
        'h-full flex flex-col bg-background',
        className
      )}
    >
      {/* Top Navigation Bar - Fixed height header */}
      <div className="flex-shrink-0 bg-card/80 backdrop-blur-xl border-b border-border z-30">
        <div className="flex items-center justify-between px-4 py-3">
          {/* Left Section - Back Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={onBackToGallery}
            className="transition-all"
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Gallery
          </Button>

          {/* Center Section - Title */}
          <div className="flex flex-col items-center">
            <h1 className="text-sm font-semibold text-foreground">Video Preview</h1>
            {isReady && playerState.segments.length > 0 && (
              <span className="text-xs text-muted-foreground">
                {playerState.segments.length} slides • {Math.floor(playerState.duration / 60)}:{String(Math.floor(playerState.duration % 60)).padStart(2, '0')}
              </span>
            )}
          </div>

          {/* Right Section - Actions */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleFullscreen}
              className="transition-all"
              title={isFullscreen ? 'Exit Fullscreen (F)' : 'Fullscreen (F)'}
            >
              {isFullscreen ? (
                <Minimize2 className="h-4 w-4" />
              ) : (
                <Maximize2 className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Video Player Container - Takes remaining space */}
      <div className="flex-1 min-h-0 flex flex-col bg-muted/10">
        {/* Video iframe - flex-1 to take available space */}
        <div className="flex-1 relative">
          <iframe
            ref={iframeRef}
            src={playerUrl}
            className="w-full h-full border-0 bg-white"
            title="Video Player"
            allow="fullscreen"
            sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-presentation"
          />

          {/* Loading State - Overlays the iframe */}
          {!isReady && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/95 backdrop-blur-sm z-10">
              <div className="text-foreground text-lg font-medium animate-pulse">
                Loading video player...
              </div>
            </div>
          )}
        </div>

        {/* Controls - Below video, not overlapping */}
        {isReady && (
          <div className="flex-shrink-0">
            <VideoPlayerControls
              isPlaying={playerState.isPlaying}
              currentTime={playerState.currentTime}
              duration={playerState.duration}
              currentSlide={playerState.currentSlide}
              segments={playerState.segments}
              playbackRate={playerState.playbackRate}
              onPlayPause={handlePlayPause}
              onRestart={handleRestart}
              onSeek={handleSeek}
              onSlideChange={handleSlideChange}
              onPreviousSlide={handlePreviousSlide}
              onNextSlide={handleNextSlide}
              onSpeedChange={handleSpeedChange}
            />
          </div>
        )}
      </div>
    </div>
  );
};
